import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BALANCE, type EntranceId, type GearId, type Plan } from '@il-colpo/engine';
import { Badge, Button, Panel } from '../../ui/index.ts';
import { Blueprint } from '../blueprint/Blueprint.tsx';
import {
  ALL_GEAR,
  EMPTY_DRAFT,
  chooseEntrance,
  draftProblem,
  estimateDraftMs,
  fromPlan,
  gearSpent,
  pressRoom,
  selectableRooms,
  toPlan,
  toggleGear,
  type Draft,
} from '../blueprint/planBuilder.ts';
import { clock } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import { EventFeed } from '../hud/EventFeed.tsx';
import { PhrasePad } from '../hud/PhrasePad.tsx';
import { TeamList } from '../hud/TeamList.tsx';
import styles from './Planning.module.css';

const ENTRANCES: readonly EntranceId[] = ['roof', 'sewer', 'service'];

export function Planning() {
  const { view } = useGame();
  const planning = view.planning;
  if (!planning) return null;
  return (
    <div className={styles['layout']}>
      <div className={styles['main']}>
        {view.you.role === 'mastermind' && planning.substage === 'awaiting' ? (
          <Builder />
        ) : (
          <Proposal />
        )}
      </div>
      <aside className={styles['side']}>
        <Panel tone="night">
          <TeamList compact />
        </Panel>
        <EventFeed limit={6} />
        <PhrasePad />
      </aside>
    </div>
  );
}

/** What everyone sees once the mastermind has proposed: the plan, the numbers, the vote. */
function Proposal() {
  const { t } = useTranslation('game');
  const { view, send, g } = useGame();
  const planning = view.planning;
  if (!planning) return null;
  const proposal = planning.proposal;
  const iVoted = view.you.id in planning.votes;
  const mastermind = view.you.role === 'mastermind';

  return (
    <>
      <Panel tone="blueprint" title={t('planning.title')}>
        <Blueprint
          map={planning.map}
          layers={planning.layers.rooms}
          guarded={planning.layers.guardedCorridors}
          entrance={proposal?.entrance ?? null}
          route={proposal?.route ?? []}
          detours={proposal?.detours ?? []}
        />
      </Panel>
      {proposal ? (
        <Panel tone="lamp" title={t('planning.proposal')}>
          <PlanSummary plan={proposal} estimateMs={planning.estimateMs} />
          <ul className={styles['votes']}>
            {view.players.map((p) => {
              const vote = planning.votes[p.id];
              return (
                <li key={p.id}>
                  <span>{p.name}</span>{' '}
                  <Badge tone={vote === undefined ? 'neutral' : vote ? 'green' : 'pink'}>
                    {vote === undefined
                      ? t('planning.notVoted')
                      : vote
                        ? t('planning.yes')
                        : t('planning.no')}
                  </Badge>
                </li>
              );
            })}
          </ul>
          {planning.substage === 'voting' && !iVoted ? (
            <div className={styles['actions']}>
              <Button icon="check" onClick={() => send({ type: 'plan.vote', approve: true })}>
                {t('planning.approve')}
              </Button>
              <Button
                variant="danger"
                icon="close"
                onClick={() => send({ type: 'plan.vote', approve: false })}
              >
                {t('planning.reject')}
              </Button>
            </div>
          ) : null}
          {planning.substage === 'tiebreak' ? (
            mastermind ? (
              <div className={styles['actions']}>
                <p>{t('planning.tie')}</p>
                <Button onClick={() => send({ type: 'plan.tiebreak', decision: 'approve' })}>
                  {t('planning.approve')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => send({ type: 'plan.tiebreak', decision: 'revise' })}
                >
                  {t('planning.revise')}
                </Button>
              </div>
            ) : (
              <p>{t('planning.waitingTie')}</p>
            )
          ) : null}
        </Panel>
      ) : (
        <Panel tone="night">
          <p>
            {planning.rejections > 0
              ? t('planning.rejected', { count: planning.rejections })
              : t('planning.waiting', { who: g('roles.mastermind') })}
          </p>
        </Panel>
      )}
    </>
  );
}

function PlanSummary({ plan, estimateMs }: { plan: Plan; estimateMs: number | null }) {
  const { t } = useTranslation('game');
  const { g, view } = useGame();
  return (
    <dl className={styles['summary']}>
      <dt>{t('planning.entrance')}</dt>
      <dd>{g(`entrances.${plan.entrance}.name`)}</dd>
      <dt>{t('planning.route')}</dt>
      <dd>{plan.route.map((r) => g(`rooms.${r}`)).join(' → ')}</dd>
      <dt>{t('planning.alcoves')}</dt>
      <dd>
        {plan.detours.length > 0
          ? plan.detours.map((d) => g(`rooms.${d.room}`)).join(', ')
          : t('planning.none')}
      </dd>
      <dt>{t('planning.gear')}</dt>
      <dd>
        {plan.gear.length > 0
          ? plan.gear.map((x) => g(`gear.${x}.name`)).join(', ')
          : t('planning.none')}
      </dd>
      {estimateMs !== null ? (
        <>
          <dt>{t('planning.estimate')}</dt>
          <dd>
            {t('planning.estimateValue', {
              work: clock(estimateMs),
              timer: clock(view.game.timerMs),
            })}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

/** The mastermind's table: pick an entrance, tap rooms to draw the route, tap alcoves, buy gear. */
function Builder() {
  const { t } = useTranslation('game');
  const { view, client, send, g } = useGame();
  const planning = view.planning;
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const map = planning?.map;
  const estimates = planning?.layers.estimateMs ?? null;

  const problem = useMemo(() => (map ? draftProblem(map, draft) : 'noEntrance'), [map, draft]);
  const selectable = useMemo(
    () => (map ? selectableRooms(map, draft) : new Set<string>()),
    [map, draft],
  );
  if (!planning || !map) return null;
  const estimate = estimates ? estimateDraftMs(map, estimates, draft) : null;
  const timer = view.game.timerMs;
  const plan = toPlan(draft);
  const hint = client.suggestPlan();

  return (
    <>
      <Panel tone="blueprint" title={t('planning.build')}>
        <div className={styles['entrances']} role="group" aria-label={t('planning.entrance')}>
          {ENTRANCES.map((e) => (
            <Button
              key={e}
              variant="secondary"
              selected={draft.entrance === e}
              onClick={() => setDraft(chooseEntrance(map, draft, e))}
            >
              {g(`entrances.${e}.name`)}
            </Button>
          ))}
        </div>
        {draft.entrance ? (
          <p className={styles['hint']}>{g(`entrances.${draft.entrance}.effect`)}</p>
        ) : null}
        <Blueprint
          map={map}
          layers={planning.layers.rooms}
          guarded={planning.layers.guardedCorridors}
          entrance={draft.entrance}
          route={draft.route}
          detours={draft.detours}
          selectable={selectable}
          onSelect={(room) => setDraft((d) => pressRoom(map, d, room))}
        />
        <p className={styles['hint']}>
          {draft.entrance ? t('planning.howTo') : t('planning.pickEntrance')}
        </p>
      </Panel>

      <Panel
        tone="lamp"
        title={t('planning.gearTitle', { spent: gearSpent(draft), budget: BALANCE.gearBudget })}
      >
        <ul className={styles['gear']}>
          {ALL_GEAR.map((x: GearId) => (
            <li key={x}>
              <Button
                variant="secondary"
                selected={draft.gear.includes(x)}
                onClick={() => setDraft((d) => toggleGear(d, x))}
              >
                {`${g(`gear.${x}.name`)} · ${BALANCE.gearCost[x]}`}
              </Button>
              <span className={styles['gearInfo']}>{g(`gear.${x}.effect`)}</span>
            </li>
          ))}
        </ul>
        {estimate !== null ? (
          <p className={styles['estimate']}>
            {t('planning.estimateValue', { work: clock(estimate), timer: clock(timer) })}
          </p>
        ) : null}
        {problem && draft.entrance ? (
          <p className={styles['problem']} role="status">
            {g(`planProblem.${problem}`)}
          </p>
        ) : null}
        <div className={styles['actions']}>
          <Button
            icon="check"
            disabled={problem !== null || !plan}
            onClick={() => plan && send({ type: 'plan.propose', plan })}
          >
            {t('planning.propose')}
          </Button>
          <Button variant="ghost" onClick={() => setDraft(EMPTY_DRAFT)}>
            {t('planning.clear')}
          </Button>
          {hint ? (
            <Button variant="ghost" icon="spark" onClick={() => setDraft(fromPlan(hint))}>
              {t('planning.suggest')}
            </Button>
          ) : null}
        </div>
      </Panel>
    </>
  );
}
